/**
 * CardSelector
 * ------------
 * Lists EVERY saved card on a client's Stripe customer and (optionally) lets
 * the coach pick which one to run a charge on. Backed by
 * GET /coach/clients/{userId}/cards.
 *
 * Props:
 *   - userId      : parent/client user_id
 *   - value       : selected pm_id (controlled)
 *   - onChange    : (pm_id) => void — fired on selection + on auto-select
 *   - selectable  : when false, renders a read-only list (default true)
 *   - onLoaded    : (cards[], defaultPmId) => void — optional
 *   - refreshKey  : change to force a reload (e.g. after adding a card)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CreditCard, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_LABEL = {
    visa: "Visa", mastercard: "Mastercard", amex: "Amex", discover: "Discover",
    diners: "Diners", jcb: "JCB", unionpay: "UnionPay",
};

const brandOf = (b) => BRAND_LABEL[(b || "").toLowerCase()] || (b ? b : "Card");
const expOf = (c) =>
    c.exp_month && c.exp_year
        ? `exp ${String(c.exp_month).padStart(2, "0")}/${String(c.exp_year).slice(-2)}`
        : "";

export default function CardSelector({
    userId,
    value,
    onChange,
    selectable = true,
    onLoaded,
    refreshKey,
}) {
    const [cards, setCards] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/clients/${userId}/cards`, { withCredentials: true });
            const list = r.data?.cards || [];
            setCards(list);
            onLoaded?.(list, r.data?.default_pm_id);
            if (selectable && list.length > 0) {
                // Keep current selection if it still exists; else pick default.
                const stillThere = value && list.some((c) => c.pm_id === value);
                if (!stillThere) {
                    const def = list.find((c) => c.is_default) || list[0];
                    onChange?.(def.pm_id);
                }
            }
        } catch (_e) {
            setCards([]);
            onLoaded?.([], null);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, refreshKey]);

    useEffect(() => { Promise.resolve().then(load); }, [load]);

    if (loading) {
        return (
            <div data-testid="card-selector-loading" className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 py-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading cards…
            </div>
        );
    }

    if (!cards || cards.length === 0) {
        return (
            <div data-testid="card-selector-empty" className="text-[11px] text-zinc-400">
                No saved cards on file yet.
            </div>
        );
    }

    return (
        <div data-testid="card-selector" className="space-y-1.5">
            {cards.map((c) => {
                const selected = selectable && value === c.pm_id;
                const line = (
                    <>
                        <CreditCard className={`w-4 h-4 shrink-0 ${selected ? "text-brand-red" : "text-zinc-400"}`} />
                        <span className="text-sm text-white font-mono whitespace-nowrap">
                            {brandOf(c.brand)} •••• {c.last4 || "••••"}
                        </span>
                        {expOf(c) && <span className="text-[10px] text-zinc-500 whitespace-nowrap">{expOf(c)}</span>}
                        {c.is_default && (
                            <span className="text-[9px] tracking-[0.15em] uppercase bg-zinc-800 text-zinc-300 border border-zinc-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                                Default
                            </span>
                        )}
                        {selectable && selected && (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto shrink-0" />
                        )}
                    </>
                );
                if (!selectable) {
                    return (
                        <div
                            key={c.pm_id}
                            data-testid={`card-row-${c.pm_id}`}
                            className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-2 border border-zinc-800 bg-black/40"
                        >
                            {line}
                        </div>
                    );
                }
                return (
                    <button
                        key={c.pm_id}
                        type="button"
                        data-testid={`card-pick-${c.pm_id}`}
                        onClick={() => onChange?.(c.pm_id)}
                        className={`w-full flex flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-2 border text-left transition-colors ${
                            selected
                                ? "border-brand-red bg-brand-red/10"
                                : "border-zinc-800 bg-black/40 hover:border-zinc-600"
                        }`}
                    >
                        {line}
                    </button>
                );
            })}
        </div>
    );
}
